/**
 * What a stage still needs, and whose move each one is.
 *
 * The mark is a word, not only a glyph: a screen reader reading "✓ Legal name"
 * hears a tick and no meaning, and a color-blind reader looking at a red and
 * a green dot sees two dots. Both get the same sentence.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { actionFor } from './requirementActions';
import { useJump } from './jump';
import page from '../../../shared/Page.module.css';
import own from './Onboarding.module.css';
import type { Requirement } from './types';

const MARK: Record<Requirement['status'], { glyph: string; said: string; tone: string }> = {
  // The TEXT tones, not the badge ones. A tick does not want a slab of green
  // behind it; it wants to be green.
  met: { glyph: '✓', said: 'Done', tone: page.okText ?? '' },
  missing: { glyph: '✕', said: 'Blocking', tone: page.dangerText ?? '' },
  warning: { glyph: '!', said: 'Worth doing', tone: page.warnText ?? '' },
  notRequired: { glyph: '–', said: 'Not required', tone: page.neutralText ?? '' },
};

export function RequirementList({
  requirements, action,
}: {
  requirements: Requirement[];
  /** A control for rows with no specific place to go (requirementActions.ts). */
  action?: (r: Requirement) => ReactNode;
}) {
  const ctx = useJump();
  if (requirements.length === 0) return null;

  /** Where this row is done, when it is ours to do and there is a place. */
  const goTo = (r: Requirement): ReactNode => {
    if (!ctx) return null;
    const a = actionFor(r, ctx.orgId);
    if (!a) return null;
    if ('href' in a) return <Link to={a.href} className={page.btn} aria-label={`${a.label}: ${r.label}`}>{a.label}</Link>;
    return (
      <button type="button" className={page.btn} aria-label={`${a.label}: ${r.label}`} onClick={() => ctx.jump(a.jump)}>
        {a.label}
      </button>
    );
  };
  return (
    <ul className={own.reqList}>
      {requirements.map((r) => {
        const mark = MARK[r.status];
        return (
          <li key={r.key} className={own.reqRow}>
            <span className={`${own.reqMark} ${mark.tone}`} aria-hidden="true">{mark.glyph}</span>
            <span className="vh">{mark.said}: </span>
            <div className={own.reqBody}>
              <div className={own.reqLabel}>{r.label}</div>
              <div className={own.reqDetail}>
                {r.detail}
                {r.status !== 'met' && r.status !== 'notRequired' && r.owner === 'customer' && (
                  <> · <strong>Waiting on the customer</strong></>
                )}
              </div>
            </div>
            {(() => {
              // The specific place first; the caller's control (Readiness's
              // "Open stage") only where there is no specific place.
              const control = goTo(r) ?? (action ? action(r) : null);
              return control ? <div className={own.reqAction}>{control}</div> : null;
            })()}
          </li>
        );
      })}
    </ul>
  );
}
